import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";
import { API_DATE_FORMAT, HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { passOr, removeNullishFromShape } from "../../utilities/general";
import {
  composePaginationSchema,
  getNextPaginationPage,
  paginationSchema,
} from "../../schemas/pagination";

const requestSchema = yup.object({
  productId: yup.number().required(),
  pageSize: yup.number().required(),
  dateFrom: yup.date().nullable(),
  dateTo: yup.date().nullable(),
  clinicIds: yup.array().of(yup.number()).required(),
  allClinics: yup.boolean().required(),
  batch: yup.string().nullable(),
});

const responseSchema = paginationSchema.concat(
  yup.object({
    page: yup.number().required(),
    logs: yup.array().of(
      yup.object({
        id: yup.number().required(),
        clinic: yup
          .object({
            id: yup.number().nullable(),
            name: yup.string().nullable(),
          })
          .required(),
        patient: yup
          .object({
            id: yup.number().nullable(),
            fullName: yup.string().required(),
          })
          .required(),
        user: yup
          .object({
            id: yup.number().nullable(),
            fullName: yup.string().nullable(),
          })
          .required(),
        inventory: yup
          .object({
            id: yup.number().nullable(),
            batchId: yup.string().nullable(),
          })
          .required(),
        createdAt: yup.string().required(),
        action: yup.string().required(),
        actionLarge: yup.string().required(),
        totalOldUnits: yup.number().required(),
        totalNewUnits: yup.number().required(),
        units: yup.number().required(),
      }),
    ),
  }),
);

const composeResponse = (res, req, page) => {
  return {
    page,
    logs:
      res?.data?.data?.data?.map((log) => ({
        id: log.id,
        clinic: {
          id: log.clinic?.id,
          name: log.clinic?.clinic_name,
        },
        patient: {
          id: log.patient?.id,
          fullName:
            log.patient_id == "0" ? "Guest" : log.patient?.full_name || "--",
        },
        user: {
          id: log.user?.id,
          fullName: log.user?.full_name,
        },
        inventory: {
          id: log.inventory?.id,
          batchId: log.inventory?.batch_id,
        },
        createdAt: log.created,
        action: log.action || "",
        actionLarge: log.action_from_description || "",
        totalOldUnits: log.total_old_units || 0,
        totalNewUnits: log.total_new_units || 0,
        units: log.units || 0,
      })) || [],
    ...composePaginationSchema({
      response: res?.data?.data || {},
      pageSize: req.pageSize,
    }),
  };
};

export function useProductDetailedLogsQuery({ payload, options = {} }) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });

  const fromDate = passOr(req.dateFrom, null, () =>
    moment(req.dateFrom).format(API_DATE_FORMAT),
  );

  const toDate = passOr(req.dateTo, null, () =>
    moment(req.dateTo).format(API_DATE_FORMAT),
  );

  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.productDetailedLogs,
      req.productId,
      req.pageSize,
      req.dateFrom,
      req.dateTo,
      req.batch,
      ...req.clinicIds,
    ].filter(Boolean),
    queryFn: async ({ pageParam = 1 }) => {
      const res = await http.get(HTTP_ENDPOINTS.getProductDetailedLogs(), {
        params: removeNullishFromShape({
          id: req.productId,
          page: pageParam,
          pagesize: req.pageSize,
          fromDate,
          toDate,
          clinic_id: req.clinicIds,
          all_clinics: req.allClinics,
          batch: req.batch,
        }),
      });
      return responseSchema.validateSync(composeResponse(res, req, pageParam), {
        strict: true,
      });
    },
    ...options,
    getNextPageParam: getNextPaginationPage,
  });
}
